import { Box, Typography, Chip, Skeleton } from "@mui/material";
import { LEVEL_STYLES } from "../../../utils/uistyles";
import { useResponsive } from "../../../utils/mobileresponsive";
import GroupOffOutlinedIcon from "@mui/icons-material/GroupOffOutlined";

// ─── Metric Cell ─────────────────────────────────────────────────────────────

const MetricCell = ({ label, value, s, span = false, strikethrough = false }) => (
	<Box
		sx={{
			background: s.metric,
			borderRadius: "6px",
			p: { xs: "4px 6px", sm: "8px 10px" },
			gridColumn: span ? "span 2" : undefined,
			minWidth: 0,
			overflow: "hidden",
		}}
	>
		<Typography
			sx={{
				fontSize: { xs: 7.5, sm: 10 },
				fontWeight: 700,
				letterSpacing: "0.04em",
				textTransform: "uppercase",
				color: s.label,
				mb: "1px",
				whiteSpace: "nowrap",
				overflow: "hidden",
				textOverflow: "ellipsis",
			}}
		>
			{label}
		</Typography>
		<Typography
			sx={{
				fontSize: { xs: span ? 13 : 12, sm: span ? 20 : 18 },
				fontWeight: 700,
				color: strikethrough ? "text.disabled" : s.value,
				textDecoration: strikethrough ? "line-through" : "none",
				wordBreak: "break-word",
				overflowWrap: "anywhere",
				lineHeight: 1.2,
			}}
		>
			{value}
		</Typography>
	</Box>
);

// ─── Agent Card ───────────────────────────────────────────────────────────────

const AgentCard = ({ agent, s, agentMap, onClick, highlighted, selectedId, level, isAgent, loggedInName }) => {
	const sourceName = isAgent && level === "Level 1" ? loggedInName : agent.source_agent_name;
	const isHighlighted = highlighted;
	const isDimmed = selectedId && !isHighlighted;
	const levelLabel = level?.replace("Level ", "L");
	// An opted-out agent kept only so the opted-in agents beneath them still have a
	// path to the root. There is no agent here to select or drill into.
	const isPlaceholder = agent.isPlaceholder === true;

	return (
		<Box
			onClick={isPlaceholder ? undefined : onClick}
			sx={{
				width: "100%",
				boxSizing: "border-box",
				borderRadius: "12px",
				p: { xs: "8px", sm: "16px" },
				...s.card,
				cursor: isPlaceholder ? "default" : "pointer",
				transition: "0.2s ease",
				opacity: isDimmed ? 0.35 : isPlaceholder ? 0.6 : 1,
				transform: isHighlighted ? "scale(1.02)" : "scale(1)",
				border: s.card.border,
				filter: isPlaceholder ? "grayscale(1)" : "none",
				overflow: "hidden",
			}}
		>
			{/* ── Card header: name + payout badge ── */}
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: "6px",
					mb: { xs: "6px", sm: "12px" },
				}}
			>
				<Typography
					sx={{
						fontSize: { xs: 11, sm: 15 },
						fontWeight: 700,
						color: s.name,
						wordBreak: "break-word",
					}}
				>
					{levelLabel} —
				</Typography>
				<Typography
					sx={{
						fontSize: { xs: 10, sm: 13 },
						color: s.name,
						wordBreak: "break-word",
						flex: 1,
						minWidth: 0,
					}}
				>
					via <strong>{sourceName}</strong>
				</Typography>
				{isPlaceholder && (
					<Chip
						label="Excluded"
						size="small"
						sx={{
							height: 17,
							fontSize: 9.5,
							fontWeight: 700,
							bgcolor: "#ECEFF1",
							color: "#546E7A",
							flexShrink: 0,
							"& .MuiChip-label": { px: 0.75 },
						}}
					/>
				)}
			</Box>

			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
					gap: { xs: "4px", sm: "7px" },
				}}
			>
				<MetricCell label="Downline Name" value={agent.name} s={s} />
				{/* A placeholder's own figures are withheld with their identity; the
				    network count stays because it describes the agents underneath. */}
				<MetricCell label="Own Network Members" value={agent.members} s={s} />
				<MetricCell label="Own Active Members" value={isPlaceholder ? "—" : agent.application} s={s} />
				<MetricCell label="Override Contribution" value={isPlaceholder ? "—" : agent.override} s={s} />
			</Box>
		</Box>
	);
};

// ─── Skeleton Card (loading placeholder) ──────────────────────────────────────

const SkeletonCard = ({ s }) => (
	<Box
		sx={{
			width: "100%",
			boxSizing: "border-box",
			borderRadius: "12px",
			p: { xs: "8px", sm: "16px" },
			...s.card,
			border: s.card.border,
			overflow: "hidden",
		}}
	>
		<Box sx={{ display: "flex", alignItems: "center", gap: "6px", mb: { xs: "6px", sm: "12px" } }}>
			<Skeleton variant="text" width={28} height={20} />
			<Skeleton variant="text" width="50%" height={20} sx={{ flex: 1 }} />
			<Skeleton variant="rounded" width={56} height={18} sx={{ borderRadius: "20px" }} />
		</Box>
		<Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: { xs: "4px", sm: "7px" } }}>
			{[0, 1, 2, 3].map((i) => (
				<Box key={i} sx={{ background: s.metric, borderRadius: "6px", p: { xs: "4px 6px", sm: "8px 10px" } }}>
					<Skeleton variant="text" width="60%" height={12} />
					<Skeleton variant="text" width="40%" height={22} />
				</Box>
			))}
		</Box>
	</Box>
);

// ─── Empty State ───────────────────────────────────────────────────────────────

const EmptyState = ({ s, label }) => (
	<Box
		sx={{
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			justifyContent: "center",
			gap: 1,
			py: { xs: 3, sm: 5 },
			px: 2,
			borderRadius: "12px",
			border: `1px dashed ${s.line}`,
			background: "rgba(0,0,0,0.015)",
			textAlign: "center",
		}}
	>
		<GroupOffOutlinedIcon sx={{ fontSize: { xs: 22, sm: 26 }, color: s.label }} />
		<Typography sx={{ fontSize: { xs: 11, sm: 12.5 }, fontWeight: 600, color: s.label }}>No {label} downlines yet</Typography>
	</Box>
);

// ─── Level Column ─────────────────────────────────────────────────────────────

export const LevelColumn = ({
	levelKey,
	agents,
	agentMap,
	currentAgent,
	onSelect,
	highlighted,
	selectedId,
	isAgent,
	loggedInName,
	loading = false,
}) => {
	const { isMobile } = useResponsive();
	const s = LEVEL_STYLES[levelKey];
	const label = { l1: "Level 1", l2: "Level 2", l3: "Level 3" }[levelKey];

	return (
		<Box sx={{ minWidth: 0, width: "100%", boxSizing: "border-box" }}>
			{/* Column header */}
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					gap: "10px",
					mb: { xs: "8px", sm: "12px" },
					overflow: "hidden",
				}}
			>
				<Box
					sx={{
						...s.pill,
						fontSize: { xs: 10, sm: 11 },
						fontWeight: 700,
						letterSpacing: ".1em",
						textTransform: "uppercase",
						px: { xs: "10px", sm: "14px" },
						py: "4px",
						borderRadius: "20px",
						whiteSpace: "nowrap",
						flexShrink: 0,
					}}
				>
					{label}
				</Box>
				<Box sx={{ flex: 1, height: "1px", background: s.line, minWidth: 0 }} />
			</Box>

			{/* Card stack */}
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					gap: { xs: "8px", sm: "12px" },
				}}
			>
				{loading ? (
					[0, 1, 2].map((i) => <SkeletonCard key={i} s={s} />)
				) : agents.length === 0 ? (
					<EmptyState s={s} label={label} />
				) : (
					agents.map((agent, i) => (
						<AgentCard
							key={agent.ID ?? i}
							agent={agent}
							s={s}
							agentMap={agentMap}
							currentAgent={currentAgent}
							onClick={() => onSelect((prev) => (prev === agent.ID ? null : agent.ID))}
							highlighted={highlighted.has(agent.ID)}
							selectedId={selectedId}
							level={label}
							isAgent={isAgent}
							loggedInName={loggedInName}
						/>
					))
				)}
			</Box>
		</Box>
	);
};
